import { useMemo } from 'react';

import { Grid } from '@mantine/core';
import { Control, Controller } from 'react-hook-form';

import { useGetGlobalFilterQuery } from '@/store/slices/api/userApiSlice';

import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import TextInputWrapper from '@/ui/TextInputWrapper';

import { sexOptions } from '../TransmissionReportPage/ReportPatientDetails/utils';

import { CreatePatientFormValues } from './CreatePatient';

type PatientInfoFieldsProps = {
  control: Control<CreatePatientFormValues>;
};

const PatientInfoFields = ({ control }: PatientInfoFieldsProps) => {
  const { data: globalFilterData } = useGetGlobalFilterQuery();

  const clinicOptions = useMemo(
    () =>
      globalFilterData?.clinics
        .map((clinic) => ({
          label: clinic.name,
          value: clinic.id,
          desc: clinic?.parent?.name ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [globalFilterData]
  );

  return (
    <Grid>
      <Grid.Col span={12}>
        <strong>Patient Information</strong>
      </Grid.Col>

      <Grid.Col span={4}>
        <Controller
          control={control}
          name="firstName"
          render={({ field }) => <TextInputWrapper required label="First Name" {...field} />}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Controller
          control={control}
          name="middleName"
          render={({ field }) => <TextInputWrapper label="Middle Name (optional)" {...field} />}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Controller
          control={control}
          name="lastName"
          render={({ field }) => <TextInputWrapper required label="Last Name" {...field} />}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <LabelText label="Date of Birth" required variant="regular" boxProps={{ mb: 4 }} />
        <Controller
          control={control}
          name="dob"
          render={({ field }) => (
            <DatePickerWrapper
              inputProps={{ placeholder: 'MM/DD/YYYY', required: true }}
              datePickerProps={{
                id: 'dob',
                selected: field.value ? new Date(field.value) : null,
                onChange: (date: Date | null) => field.onChange(date ?? ''),
                required: true,
                isClearable: false
              }}
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <LabelText label="Sex" required variant="regular" boxProps={{ mb: 4 }} />
        <Controller
          control={control}
          name="sex"
          render={({ field }) => (
            <Dropdown
              required
              options={sexOptions}
              value={sexOptions.find((opt) => opt.value === field.value)}
              onChange={(opt) => field.onChange(opt?.value)}
              placeholder="Select"
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Controller
          control={control}
          name="mrn"
          render={({ field }) => <TextInputWrapper required label="Internal MRN" {...field} />}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <LabelText label="Clinic" required variant="regular" boxProps={{ mb: 4 }} />
        <Controller
          control={control}
          name="clinicId"
          render={({ field }) => (
            <Dropdown
              required
              options={clinicOptions}
              value={clinicOptions.find((opt) => opt.value === field.value)}
              onChange={(opt) => field.onChange(opt?.value)}
              placeholder="Select"
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );
};

export default PatientInfoFields;
